import { gql, useMutation, useQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import type { CurrencyCodes } from '@generated/graphql';
import { FeatureFlagTypes } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import type { BankAccount } from '@interfaces/bankAccount';

import { MenuItem, styled } from '@mui/material';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { useTextFieldTokens } from '@ovComponents/2-component/textField/textField.tokens';
import type { MultiCurrencyAmount } from '@ovComponents/4-module/workflowCompletion/subSteps/withdrawForm/withdrawalBreakdown';
import {
  useOrganizationFeatureFlag,
  usePermission,
} from '@providers/userContextProvider';
import { filterBankAccountsByCurrency } from '@utils/common';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Avatar, Box, IconButton, Typography, useTheme } from 'hephaestus/web';
import { DeleteForever as DeleteForeverIcon } from 'hephaestus/webIcons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddBankAccount from '../addBankAccount/addBankAccount';
import { ConfirmationModal } from '../confirmationDialog/confirmationDialog';
import { getBankIconPath } from './bankAccountIcon';

const FETCH_USER_BANK_ACCOUNTS = ({
  canReadBankAccountDisclosed,
}: {
  canReadBankAccountDisclosed: boolean;
}) => gql`
  query fetchBankAccounts($input: FetchBankAccountsInput!) {
    fetchBankAccounts(input: $input) {
      bankAccounts {
        id
        institutionNumber
        name
        bankAccountNumber
        bankAccountTransitPhysicalAddress {
          houseNumber
          unitNumber
          streetName
          city
          jurisdiction
          country
          postal
        }
        transitNumber
        currency
        ${canReadBankAccountDisclosed ? 'bankAccountNumberDisclosed transitNumberDisclosed' : ''}
      }
    }
  }
`;

const FETCH_EXTERNAL_BANK_ACCOUNTS = gql`
  query FetchCustodianBankAccounts($input: FetchCustodianBankAccountsInput!) {
    fetchCustodianBankAccounts(input: $input) {
      custodianBankAccounts {
        id
        name
        institutionNumber
        bankAccountNumber
        transitNumber
        bankAccountNumberDisclosed
        transitNumberDisclosed
      }
    }
  }
`;

const DELETE_BANK_ACCOUNT = gql`
  mutation deleteBankAccount($bankAccountId: ObjectID!) {
    deleteBankAccount(bankAccountId: $bankAccountId) {
      bankAccount {
        id
        name
      }
    }
  }
`;

type BankAccountSelectFieldProp = {
  userId: string;
  bankAccount: BankAccount | null;
  setBankAccount: (value: BankAccount | null) => void;
  discloseBankAccountNumber?: boolean;
  sx?: any;
  onBlur?: () => void;
  error?: any;
  transactionType?: 'DEPOSIT' | 'WITHDRAWAL';
  disabled?: boolean;
  options?: any;
  account?: Account | undefined;
  showSelectedIcon?: boolean;
  isMultiCurrencyAccount?: boolean;
  multiCurrencyAmounts?: MultiCurrencyAmount[];
};

const StyledMenuItem = styled(MenuItem)({
  display: 'flex',
  justifyContent: 'space-between',
});

const getMultiCurrencyAmount = (
  currency: CurrencyCodes,
  multiCurrencyAmounts: MultiCurrencyAmount[],
) => {
  return (
    multiCurrencyAmounts.find(
      (item: { currency: CurrencyCodes }) => item.currency === currency,
    )?.availableFundsToWithdrawCents ?? 0
  );
};

const BankAccountTypography = ({
  showIcon = false,
  children,
}: {
  showIcon?: boolean;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);

  const defaultFontFamily =
    'Interphases Pro, "Causten Round", Helvetica, sans-serif';

  return (
    <Typography
      variant='bodySmall'
      component='p'
      sx={
        showIcon
          ? {
              fontFamily: theme.font?.base || defaultFontFamily,
              lineHeight: 'medium',
              color: tokens.textColor,
            }
          : {
              marginTop: '3px',
              color: tokens.textColor,
            }
      }
    >
      {children}
    </Typography>
  );
};

const BankAccountDisplay = ({
  ba,
  discloseBankAccountNumber,
  showIcon = true,
  isMultiCurrencyAccount = false,
  multiCurrencyAmounts = [],
}: {
  ba: BankAccount;
  discloseBankAccountNumber: boolean;
  showIcon?: boolean;
  isMultiCurrencyAccount?: boolean;
  multiCurrencyAmounts?: MultiCurrencyAmount[];
}) => {
  const accountNumber =
    discloseBankAccountNumber && ba.bankAccountNumberDisclosed
      ? ba.bankAccountNumberDisclosed
      : ba.bankAccountNumber;

  const balance = isMultiCurrencyAccount
    ? formatMoneyValue(
        getMultiCurrencyAmount(
          ba.currency as CurrencyCodes,
          multiCurrencyAmounts,
        ),
        '$',
        2,
      )
    : '';

  return (
    <Box
      sx={
        [
          {
            display: 'flex',
            alignItems: 'center',
          },
          !showIcon ? { marginTop: '0' } : undefined,
        ] as any
      }
    >
      {showIcon && (
        <Box
          style={{ marginRight: '5px' }}
          sx={{
            mr: 1,
          }}
        >
          <Avatar size='xsmall'>
            <img
              alt='bank-account-icon'
              src={getBankIconPath(
                ba?.institutionNumber ?? '',
                ba?.transitNumberDisclosed ?? '',
              )}
              style={{
                objectFit: 'cover',
                height: '100%',
                width: '100%',
                marginLeft: '0',
              }}
            />
          </Avatar>
        </Box>
      )}

      <BankAccountTypography showIcon={showIcon}>
        {`${ba.name} ${accountNumber} ${balance}`}
      </BankAccountTypography>
    </Box>
  );
};

export const BankAccountSelectField = ({
  userId,
  bankAccount,
  discloseBankAccountNumber = false,
  sx,
  error,
  disabled,
  transactionType,
  onBlur,
  setBankAccount,
  options,
  account,
  showSelectedIcon = false,
  isMultiCurrencyAccount = false,
  multiCurrencyAmounts = [],
}: BankAccountSelectFieldProp) => {
  const selectedBankAccountId = useRef('');
  const { t } = useTranslation(['components']);
  const theme = useTheme();
  const canWriteBankAccount = usePermission('write:bank_account_basic');
  const canReadBankAccountDisclosed = usePermission(
    'read:bank_account_disclosed',
  );
  const manuallyAddBankAccountEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.ManuallyAddBankAccount,
  );
  const automatedBankAccountLinkingEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.AutomatedBankAccountLinking,
  );

  const [addBankAccount, setAddBankAccount] = useState(false);
  const [openDeleteBankModal, setOpenDeleteBankModal] = useState(false);
  const [userBankAccounts, setUserBankAccounts] = useState<BankAccount[]>([]);
  const [addedBankAccountId, setAddedBankAccountId] = useState<
    string | undefined
  >();

  const fetchExternalBankAccounts = options?.enableFetchExternalBankAccounts
    ? options?.enableFetchExternalBankAccounts
    : false;

  const { loading, refetch } = useQuery(
    fetchExternalBankAccounts
      ? FETCH_EXTERNAL_BANK_ACCOUNTS
      : FETCH_USER_BANK_ACCOUNTS({ canReadBankAccountDisclosed }),
    {
      skip: !userId,
      variables: { input: { filter: { userId } } },
      onCompleted: (data) => {
        if (fetchExternalBankAccounts) {
          const updatedBankAccounts =
            data?.fetchCustodianBankAccounts?.custodianBankAccounts;
          setUserBankAccounts(updatedBankAccounts);
        } else {
          const fetchedBankAccounts = data?.fetchBankAccounts?.bankAccounts;
          const filteredBankAccounts = filterBankAccountsByCurrency(
            fetchedBankAccounts,
            account,
          );
          setUserBankAccounts(filteredBankAccounts);
        }
      },
    },
  );

  const [deleteBankAccountMutation] = useMutation(DELETE_BANK_ACCOUNT, {
    variables: { bankAccountId: selectedBankAccountId.current },
    onCompleted: refetch,
  });

  const enableManualBankAccount: boolean =
    options?.manualbankAccountLinking || manuallyAddBankAccountEnabled;
  const enableFlinks: boolean =
    options?.automatedBankAccountLinking || automatedBankAccountLinkingEnabled;

  const setSelectedBankAccount = (bankAccountId: string) => {
    selectedBankAccountId.current = bankAccountId;
    setOpenDeleteBankModal(true);
  };

  const confirmBankAccountDeletion = () => {
    deleteBankAccountMutation();
    setOpenDeleteBankModal(false);
    setBankAccount(null);
  };

  useEffect(() => {
    if (addedBankAccountId) {
      setBankAccountById(addedBankAccountId);
      setAddedBankAccountId(undefined);
    }
  }, [userBankAccounts, addedBankAccountId]);

  const setBankAccountById = (id: string) => {
    const found = userBankAccounts.find(
      (bank: { id: string }) => bank.id === id,
    );

    if (found) {
      setBankAccount(found);
    }
  };

  const handleBankAccountSelect = (value: string) => {
    // If the selected value is already selected, unselect it
    if (value === bankAccount?.id) {
      setBankAccount(null);
    } else if (value === 'addBank') {
      setAddBankAccount(true);
    } else {
      setBankAccountById(value);
    }
  };

  const handleBankAccountClick = (item: BankAccount) => {
    // If the clicked value is already selected, unselect it
    if (bankAccount?.id === item?.id) {
      setBankAccount(null);
    }
  };

  const renderValue = (ba: BankAccount | null) => {
    if (!ba) return '';

    return (
      <BankAccountDisplay
        ba={ba}
        discloseBankAccountNumber={discloseBankAccountNumber}
        showIcon={showSelectedIcon}
        isMultiCurrencyAccount={isMultiCurrencyAccount}
        multiCurrencyAmounts={multiCurrencyAmounts}
      />
    );
  };

  return (
    <>
      <SelectField
        fullWidth
        sx={sx as any}
        testId='select-bank-account'
        onChange={(e: any) => handleBankAccountSelect(e.target.value)}
        label={
          translateBackend(options?.bankAccount.label) ||
          t('components:bankAccountSelect.addBankModal.bankAccount')
        }
        value={bankAccount?.id || ''}
        onBlur={onBlur}
        disabled={disabled}
        error={error}
        renderValue={() => renderValue(bankAccount)}
      >
        {!loading &&
          userBankAccounts.map((item: BankAccount) => (
            <StyledMenuItem
              key={item.id}
              value={item.id}
              data-testid={`bank-account-item-${item.name}`}
              onClick={() => handleBankAccountClick(item)}
            >
              <BankAccountDisplay
                ba={item}
                discloseBankAccountNumber={discloseBankAccountNumber}
              />

              {isMultiCurrencyAccount && (
                <BankAccountTypography>
                  {formatMoneyValue(
                    getMultiCurrencyAmount(
                      item.currency as CurrencyCodes,
                      multiCurrencyAmounts,
                    ),
                    '$',
                    2,
                  )}

                  {` ${item.currency}`}
                </BankAccountTypography>
              )}

              {canWriteBankAccount && (
                <IconButton
                  aria-label='delete'
                  onClick={() => setSelectedBankAccount(item.id)}
                >
                  <DeleteForeverIcon fontSize='inherit' />
                </IconButton>
              )}
            </StyledMenuItem>
          ))}

        {(enableManualBankAccount || enableFlinks) && (
          <MenuItem
            sx={{ color: theme.color.primary } as any}
            data-testid='add-bank-account'
            value='addBank'
          >
            {t('components:bankAccountSelect.addBank')}
          </MenuItem>
        )}
      </SelectField>

      <AddBankAccount
        data-testid='add-bank-account'
        handleClose={() => setAddBankAccount(false)}
        open={addBankAccount}
        userId={userId}
        bankAccounts={loading ? [] : userBankAccounts}
        afterCreate={async (newBankAccountId: string) => {
          await refetch();
          setAddBankAccount(false);
          setAddedBankAccountId(newBankAccountId);
        }}
        transactionType={transactionType}
        options={options}
      />

      <ConfirmationModal
        open={openDeleteBankModal}
        onCancel={() => setOpenDeleteBankModal(false)}
        onConfirm={confirmBankAccountDeletion}
        title={t('components:bankAccountSelect.deleteBankAccountModal.title')}
        bodyText={t('components:bankAccountSelect.deleteBankAccountModal.body')}
      />
    </>
  );
};
